import { useTranslation } from "react-i18next";
import React, { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { toast } from "sonner";
import { formatCurrency } from "@/lib/utils";

const MONTHLY_PRODUCT_ID = "prod_TFVe92KUvlKJGg";
const ANNUAL_PRODUCT_ID = "prod_TFVe3ZSPeoxlVA";

// Stripe pricing
const MONTHLY_PRICE = 3.99;
const ANNUAL_PRICE = 20.11;

// Apple IAP pricing (via RevenueCat)
const APPLE_MONTHLY_PRICE = 5.99;
const APPLE_ANNUAL_PRICE = 34.99;

interface DataPoint {
  date: string;
  value: number;
}

type MetricType = "mrr" | "lifetime_users" | "total_users" | "token_spend";

const AdminAnalyticsGraph = () => {
  const { t } = useTranslation();

  const [metric, setMetric] = useState<MetricType>("mrr");
  const [data, setData] = useState<DataPoint[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalyticsData();
  }, [metric]);

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);

      if (metric === "mrr") {
        await fetchMRRData();
      } else if (metric === "lifetime_users") {
        await fetchLifetimeUsersData();
      } else if (metric === "total_users") {
        await fetchTotalUsersData();
      } else if (metric === "token_spend") {
        await fetchTokenSpendData();
      }
    } catch (error) {
      console.error("[AdminAnalytics] Error fetching data:", error);
      toast.error(t('notifications.toast.failed_to_load_analytics_data'));
    } finally {
      setLoading(false);
    }
  };

  const fetchMRRData = async () => {
    // Get list of active (non-deleted) user IDs
    const { data: activeUserIds } = await supabase.rpc('get_active_user_ids_for_admin');
    
    // Get all profiles with subscription info, excluding deleted accounts
    const { data: profiles, error } = await supabase
      .from('profiles')
      .select('created_at, is_premium, subscription_product_id, is_lifetime_premium, id')
      .in('id', activeUserIds || [])
      .order('created_at', { ascending: true });

    if (error) throw error;

    // Group by month and calculate MRR
    const mrrByMonth = new Map<string, { monthly: number; annual: number }>();

    profiles?.forEach((profile) => {
      if (!profile.is_premium) return;
      
      const date = new Date(profile.created_at);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

      if (!mrrByMonth.has(monthKey)) {
        mrrByMonth.set(monthKey, { monthly: 0, annual: 0, appleMonthly: 0, appleAnnual: 0 });
      }

      const counts = mrrByMonth.get(monthKey)!;

      if (profile.is_lifetime_premium) {
        // Don't count lifetime users in MRR (one-time payment)
        return;
      } else if (profile.subscription_product_id === MONTHLY_PRODUCT_ID) {
        counts.monthly++;
      } else if (profile.subscription_product_id === ANNUAL_PRODUCT_ID) {
        counts.annual++;
      } else if (profile.subscription_product_id?.startsWith('revenuecat_')) {
        // Apple IAP via RevenueCat
        const productId = profile.subscription_product_id;
        if (productId.includes('monthly') || productId.includes('AIPro1a') || 
            productId.includes('prod717a297333')) {
          counts.appleMonthly++;
        } else if (productId.includes('annual') || productId.includes('AIPro1b') || 
                   productId.includes('prodfeebb85aa5')) {
          counts.appleAnnual++;
        }
        // Note: Lifetime RevenueCat subscriptions are filtered out by is_lifetime_premium check above
      }
    });

    // Calculate cumulative MRR over time
    const dataPoints: DataPoint[] = [];
    let cumulativeMonthly = 0;
    let cumulativeAnnual = 0;
    let cumulativeAppleMonthly = 0;
    let cumulativeAppleAnnual = 0;

    Array.from(mrrByMonth.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .forEach(([month, counts]) => {
        cumulativeMonthly += counts.monthly || 0;
        cumulativeAnnual += counts.annual || 0;
        cumulativeAppleMonthly += counts.appleMonthly || 0;
        cumulativeAppleAnnual += counts.appleAnnual || 0;

        // MRR = (Stripe monthly * price) + (Stripe annual * price / 12) + 
        //       (Apple monthly * price) + (Apple annual * price / 12)
        const mrr = 
          (cumulativeMonthly * MONTHLY_PRICE) + 
          (cumulativeAnnual * (ANNUAL_PRICE / 12)) +
          (cumulativeAppleMonthly * APPLE_MONTHLY_PRICE) +
          (cumulativeAppleAnnual * (APPLE_ANNUAL_PRICE / 12));

        dataPoints.push({
          date: month,
          value: Math.round(mrr * 100) / 100, // Round to 2 decimal places
        });
      });

    setData(dataPoints);
  };

  const fetchLifetimeUsersData = async () => {
    // Get list of active (non-deleted) user IDs
    const { data: activeUserIds } = await supabase.rpc('get_active_user_ids_for_admin');
    
    // Get all lifetime users over time, excluding deleted accounts
    const { data: profiles, error } = await supabase
      .from('profiles')
      .select('created_at, id')
      .eq('is_lifetime_premium', true)
      .in('id', activeUserIds || [])
      .order('created_at', { ascending: true });

    if (error) throw error;

    // Group by month and count cumulative lifetime users
    const usersByMonth = new Map<string, number>();

    profiles?.forEach((profile) => {
      const date = new Date(profile.created_at);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

      usersByMonth.set(monthKey, (usersByMonth.get(monthKey) || 0) + 1);
    });

    // Calculate cumulative count
    const dataPoints: DataPoint[] = [];
    let cumulative = 0;

    Array.from(usersByMonth.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .forEach(([month, count]) => {
        cumulative += count;
        dataPoints.push({
          date: month,
          value: cumulative,
        });
      });

    setData(dataPoints);
  };

  const fetchTotalUsersData = async () => {
    // Get list of active (non-deleted) user IDs
    const { data: activeUserIds } = await supabase.rpc('get_active_user_ids_for_admin');
    
    // Get all users over time, excluding deleted accounts
    const { data: profiles, error } = await supabase
      .from('profiles')
      .select('created_at, id')
      .in('id', activeUserIds || [])
      .order('created_at', { ascending: true });

    if (error) throw error;

    // Group by month and count cumulative users
    const usersByMonth = new Map<string, number>();

    profiles?.forEach((profile) => {
      const date = new Date(profile.created_at);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

      usersByMonth.set(monthKey, (usersByMonth.get(monthKey) || 0) + 1);
    });

    // Calculate cumulative count
    const dataPoints: DataPoint[] = [];
    let cumulative = 0;

    Array.from(usersByMonth.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .forEach(([month, count]) => {
        cumulative += count;
        dataPoints.push({
          date: month,
          value: cumulative,
        });
      });

    setData(dataPoints);
  };

  const fetchTokenSpendData = async () => {
    // Get all free tier token usage over time
    const { data: tokenUsage, error } = await (supabase as any)
      .from('free_tier_token_usage')
      .select('created_at, cost_usd')
      .order('created_at', { ascending: true });

    if (error) throw error;

    // Group by day and sum costs
    const spendByDay = new Map<string, number>();

    tokenUsage?.forEach((usage: any) => {
      const date = new Date(usage.created_at);
      const dayKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

      spendByDay.set(dayKey, (spendByDay.get(dayKey) || 0) + Number(usage.cost_usd));
    });

    // Create data points
    const dataPoints: DataPoint[] = Array.from(spendByDay.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([day, cost]) => ({
        date: day,
        value: Math.round(cost * 100) / 100, // Round to 2 decimal places
      }));

    setData(dataPoints);
  };

  const getMetricLabel = () => {
    switch (metric) {
      case "mrr":
        return "Monthly Recurring Revenue (MRR)";
      case "lifetime_users":
        return "Lifetime Premium Users";
      case "total_users":
        return "Total Users";
      case "token_spend":
        return "Free Tier Daily Token Spending (Last 30 Days)";
      default:
        return "Metric";
    }
  };

  const formatYAxis = (value: number) => {
    if (metric === "mrr" || metric === "token_spend") {
      return formatCurrency(value);
    }
    return value.toString();
  };

  const formatTooltip = (value: number) => {
    if (metric === "mrr") {
      return [formatCurrency(value), "MRR"];
    }
    if (metric === "token_spend") {
      return [formatCurrency(value), "Daily Spend"];
    }
    return [value, metric === "lifetime_users" ? "Lifetime Users" : "Total Users"];
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Analytics</CardTitle>
          <Select value={metric} onValueChange={(value) => setMetric(value as MetricType)}>
            <SelectTrigger className="w-[240px]">
              <SelectValue placeholder={t('forms.placeholders.select_metric')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="mrr">{t('ui.text.monthly_recurring_revenue')}</SelectItem>
              <SelectItem value="lifetime_users">{t('ui.text.lifetime_users')}</SelectItem>
              <SelectItem value="total_users">{t('ui.text.total_users')}</SelectItem>
              <SelectItem value="token_spend">{t('ui.text.free_tier_token_spending')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center h-[300px]">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : data.length === 0 ? (
          <div className="flex items-center justify-center h-[300px] text-muted-foreground">{t('ui.text.no_data_available')}</div>
        ) : (
          <div>
            <div className="mb-4">
              <p className="text-sm text-muted-foreground">{getMetricLabel()}</p>
              <p className="text-3xl font-bold">
                {metric === "mrr" || metric === "token_spend"
                  ? formatCurrency(data[data.length - 1]?.value || 0)
                  : data[data.length - 1]?.value || 0
                }
              </p>
              {metric === "token_spend" && data.length > 0 && (
                <p className="text-sm text-muted-foreground mt-1">{t('ui.text.budget_16_67_day_last_30_days_shown')}</p>
              )}
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={data}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis 
                  dataKey="date" 
                  className="text-xs"
                  tick={{ fontSize: 12 }}
                />
                <YAxis 
                  tickFormatter={formatYAxis}
                  className="text-xs"
                  tick={{ fontSize: 12 }}
                />
                <Tooltip 
                  formatter={formatTooltip}
                  contentStyle={{
                    backgroundColor: 'hsl(var(--background))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px',
                  }}
                />
                <Line 
                  type="monotone" 
                  dataKey="value" 
                  stroke="hsl(var(--primary))" 
                  strokeWidth={2}
                  dot={{ fill: 'hsl(var(--primary))', r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminAnalyticsGraph;
